import type { Chart, ChartTypeRegistry, TooltipItem, TooltipModel, TooltipOptions } from 'chart.js'

type TooltipLabelCallback<TType extends keyof ChartTypeRegistry> = (
  tooltipItem: TooltipItem<TType>,
) => string | string[] | undefined

const EXTERNAL_TOOLTIP_ELEMENT_ID = 'chartjs-external-tooltip'

const getOrCreateTooltipElement = (): HTMLDivElement => {
  let tooltipElement = document.getElementById(EXTERNAL_TOOLTIP_ELEMENT_ID) as HTMLDivElement | null

  if (tooltipElement) return tooltipElement

  tooltipElement = document.createElement('div')
  tooltipElement.id = EXTERNAL_TOOLTIP_ELEMENT_ID
  tooltipElement.style.position = 'absolute'
  tooltipElement.style.pointerEvents = 'none'
  tooltipElement.style.background = 'rgba(15, 23, 42, 0.92)'
  tooltipElement.style.border = '1px solid rgba(148, 163, 184, 0.35)'
  tooltipElement.style.borderRadius = '8px'
  tooltipElement.style.color = '#ffffff'
  tooltipElement.style.padding = '10px'
  tooltipElement.style.fontFamily = 'Inter, sans-serif'
  tooltipElement.style.fontSize = '12px'
  tooltipElement.style.lineHeight = '1.4'
  tooltipElement.style.whiteSpace = 'normal'
  tooltipElement.style.maxWidth = '320px'
  tooltipElement.style.opacity = '0'
  tooltipElement.style.zIndex = '9999'
  tooltipElement.style.transform = 'translate(-50%, calc(-100% - 12px))'
  document.body.appendChild(tooltipElement)

  return tooltipElement
}

const renderTooltipContent = (
  tooltipElement: HTMLDivElement,
  tooltipModel: TooltipModel<any>,
): void => {
  tooltipElement.innerHTML = ''

  const titleLines = tooltipModel.title ?? []
  const bodyLines = (tooltipModel.body ?? []).flatMap((item) => item.lines)

  if (titleLines.length > 0) {
    const title = document.createElement('div')
    title.style.fontWeight = '700'
    title.style.marginBottom = bodyLines.length > 0 ? '4px' : '0'
    title.textContent = titleLines.join(' ')
    tooltipElement.appendChild(title)
  }

  bodyLines.forEach((line) => {
    const row = document.createElement('div')
    row.textContent = line
    tooltipElement.appendChild(row)
  })
}

const externalTooltipHandler = function (
  this: TooltipModel<any>,
  context: { chart: Chart; tooltip: TooltipModel<any> },
): void {
  const { chart, tooltip } = context
  const tooltipElement = getOrCreateTooltipElement()

  if (tooltip.opacity === 0) {
    tooltipElement.style.opacity = '0'
    return
  }

  renderTooltipContent(tooltipElement, tooltip)

  const chartRect = chart.canvas.getBoundingClientRect()
  const viewportPadding = 12
  const centeredLeft = window.pageXOffset + chartRect.left + tooltip.caretX
  const top = window.pageYOffset + chartRect.top + tooltip.caretY

  tooltipElement.style.opacity = '1'
  tooltipElement.style.left = `${centeredLeft}px`
  tooltipElement.style.top = `${top}px`

  const tooltipRect = tooltipElement.getBoundingClientRect()
  if (tooltipRect.left < viewportPadding) {
    tooltipElement.style.left = `${window.pageXOffset + viewportPadding}px`
    tooltipElement.style.transform = 'translate(0, calc(-100% - 12px))'
    return
  }

  if (tooltipRect.right > window.innerWidth - viewportPadding) {
    tooltipElement.style.left = `${window.pageXOffset + window.innerWidth - viewportPadding}px`
    tooltipElement.style.transform = 'translate(-100%, calc(-100% - 12px))'
    return
  }

  tooltipElement.style.transform = 'translate(-50%, calc(-100% - 12px))'
}

export const getExternalTooltipOptions = <TType extends keyof ChartTypeRegistry>(
  tooltipCallback?: TooltipLabelCallback<TType>,
): TooltipOptions<TType> => {
  const tooltipOptions = {
    enabled: false,
    external: externalTooltipHandler as TooltipOptions<TType>['external'],
  } as TooltipOptions<TType>

  if (tooltipCallback) {
    ;(tooltipOptions as any).callbacks = {
      label: (tooltipItem: TooltipItem<TType>) => tooltipCallback(tooltipItem),
    }
  }

  return tooltipOptions
}
